/**
 * Auto-Redline Types
 *
 * Type definitions for the auto-redline feature that enables AI-suggested
 * document edits with visual previews and one-click application to Word documents
 */

import type { TrackChangesMode } from "../helpers/word";

/**
 * Edit operation types that determine how text modifications are applied
 */
export type EditPosition = "replace" | "before" | "after";

/**
 * Operation type at the section level (AutoRedlineOutput)
 * - 'edit': Standard text modifications using searchPhrase (DEFAULT)
 * - 'insert': Create new paragraphs with tracked changes using paragraphId
 * - 'delete': Remove entire paragraphs with tracked changes using paragraphId
 */
export type EditOperationType = "edit" | "insert" | "delete";

/**
 * Status states for individual edit operations
 */
export type EditStatus = "pending" | "applying" | "applied" | "failed";

/**
 * List-structure change actions (v1). Applied via the Office.js List API as
 * tracked changes, independent of any paragraph text change.
 * - 'setLevel'      : set the target paragraph's list level (must already be a list item)
 * - 'newList'       : start a fresh list on the target paragraph (this is how we "restart")
 * - 'continueList'  : join the list that `anchorParagraphId` belongs to, at `level`
 * - 'detach'        : remove the target paragraph from its list (back to plain text)
 */
export type ListChangeAction = "setLevel" | "newList" | "continueList" | "detach";

/**
 * One list level's marker definition, lifted verbatim from the document's
 * `numbering.xml` by the backend.
 *
 * `startNewList()` produces a list with NO level definitions, so whatever level
 * the paragraph lands on renders with Word's default marker (a bullet) unless we
 * define it first. Office.js cannot READ an existing list's `w:numFmt` /
 * `w:lvlText` on Word Online (`ListLevel` is WordApiDesktop-only), so the backend
 * — which parses `numbering.xml` anyway — ships the source list's scheme and we
 * replay it onto the fork. See `numberFormatMapping.ts` for the OOXML → Office.js
 * translation.
 */
export interface NumberFormatLevel {
  /** List level index 0-8. */
  level: number;
  /** OOXML `w:numFmt`, e.g. "decimal" | "lowerLetter" | "upperRoman" | "bullet". */
  numFmt: string;
  /** OOXML `w:lvlText` marker template, e.g. "%1." | "%1.%2" | "(%1)". Bullet levels carry the glyph. */
  lvlText: string;
}

/**
 * Structured list-structure change carried by an EditOperation. Rides on the
 * existing edit/insert operation types (never a new operationType) and is
 * orthogonal to the text change: an edit may change text, structure, or both.
 */
export interface ListChange {
  /** Which structural change to apply */
  action: ListChangeAction;
  /** Target list level 0-8 (setLevel / continueList / newList) */
  level?: number;
  /**
   * continueList only: 0-based `body.paragraphs` index of a paragraph ALREADY in
   * the target list. Same index space as the section-level `paragraphId`; the FE
   * reads that anchor's LIVE list id at apply time (numId is never transported).
   */
  anchorParagraphId?: number;
  /**
   * `newList` only: the marker scheme to define on the list we create, one entry
   * per level the SOURCE list defined. Absent/null when the source paragraph is
   * not numbered (or the doc had no numbering.xml) — we then fall back to plain
   * decimal. The backend emits explicit `null`, so guard with `!= null`.
   */
  numberFormat?: NumberFormatLevel[] | null;
}

/**
 * OUTPUT-LEVEL numbering-change kinds (v2). Distinct from the edit-level
 * `ListChangeAction` union above: this drives a display-only Word-Ribbon-style
 * icon in the numbering gutter (it never applies anything), whereas `ListChange`
 * is the imperative apply instruction carried by an EditOperation.
 *
 * The four strings are a SHARED CONTRACT and must match the backend `Literal`
 * byte-for-byte:
 * - 'indentIn'  : the paragraph is indented one list level deeper
 * - 'indentOut' : the paragraph is outdented one list level
 * - 'addToList' : the paragraph is added to the (preceding) list
 * - 'newList'   : the paragraph starts a fresh list
 */
export type NumberingChangeKind = "indentIn" | "indentOut" | "addToList" | "newList";

/**
 * OUTPUT-LEVEL structured numbering change (sibling of `numberingPreview` on
 * AutoRedlineOutput). Display-only: the FE maps `kind` to a Fluent ribbon icon +
 * tooltip and echoes `currentLabel` as a muted prior-state label ("was 2.b.").
 *
 * MUTUALLY EXCLUSIVE with `numberingPreview` — the backend emits at most one of
 * the two. Render precedence: `numberingChange` (icon gutter) →
 * `numberingPreview` (HTML gutter) → single-column (no gutter).
 */
export interface NumberingChange {
  /** Which numbering change occurred (drives icon + tooltip). */
  kind: NumberingChangeKind;
  /** Pre-change label ("2.b."), echoed as prior-state ("was 2.b."). */
  currentLabel?: string;
  /** List level before the change (optional, informational). */
  fromLevel?: number;
  /** List level after the change (optional, informational). */
  toLevel?: number;
}

/**
 * Individual edit operation as provided by the backend
 * Follows the exact structure from Word_editing_tool.md
 */
export interface EditOperation {
  /** Unique identifier for this edit */
  editId: string;

  /**
   * Unique phrase to locate in Word document (<250 chars, guaranteed by backend)
   * Used for document-wide search to avoid Word Desktop 256-character limit
   */
  searchPhrase: string;

  /** Text to replace (empty string for insertions) */
  targetText: string;

  /** New text to insert or replace with */
  replacementText: string;

  /** Type of edit operation */
  position: EditPosition;

  /** Match whole words only (default: false) */
  matchWholeWord?: boolean;

  /**
   * Which occurrence of targetText to replace within the searchPhrase (1-indexed, default: 1)
   * Scope: WITHIN the searchPhrase only
   */
  targetOccurrence?: number;

  /**
   * Which occurrence of searchPhrase to select (1-indexed, REQUIRED)
   *
   * Scope depends on paragraphId:
   * - If paragraphId is provided: Counts within that specific paragraph only
   * - If paragraphId is NOT provided: Counts across entire document (backward compatible)
   *
   * Backend calculates this value based on the search scope being used
   */
  searchPhraseOccurrence: number;

  /** Whether sync is required before applying (backend detected overlap) */
  requiresSync?: boolean;

  /**
   * Structured list-structure change (level / list membership / restart / detach).
   * Present on 'edit' (change an existing paragraph's structure) or 'insert' (set
   * the new paragraph's list membership). When present with an empty `searchPhrase`
   * the edit is structure-only and no text search runs.
   *
   * The backend emits this key on EVERY edit (`null` when there is no structure
   * change), so `null` is accepted and treated as absent by `resolveListChange`.
   */
  listChange?: ListChange | null;
}

/**
 * Represents a group of edits to be applied together
 */
export interface EditGroup {
  /** Whether this group can be batched or needs individual processing */
  isBatch: boolean;
  /** The edits in this group */
  edits: EditOperation[];
}

/**
 * Internal structure for tracking search operations during batch processing
 * NEW: Simplified for document-wide search approach (no paragraph range tracking)
 */
export interface SearchOperation {
  /** The edit operation being processed */
  edit: EditOperation;
  /** Document-wide search results (all occurrences of searchPhrase) */
  searchResults: Word.RangeCollection;
  /** Target text search results for replace operations */
  targetSearch?: Word.RangeCollection;
  /** The selected occurrence range based on searchPhraseOccurrence */
  searchPhraseRange?: Word.Range;
  /** Whether fallback (strict) search was used */
  usedFallback?: boolean;
}

/**
 * Complete output from the auto-redline tool
 * This is the structure received from the backend in tool_use_result chunks
 */
export interface AutoRedlineOutput {
  /** Section name/title being edited */
  sectionName: string;

  /** HTML preview with changes styled using <s> and <u> tags */
  previewText: string;

  /**
   * Clean paragraph text for preview and debugging purposes
   * NOTE: NOT used for primary search due to Word Desktop 256-character limit
   * May exceed 255 characters - that's OK, only used for preview generation
   * Actual searching uses searchPhrase with searchPhraseOccurrence in EditOperation
   */
  searchParagraph: string;

  /**
   * Paragraph index (0-based) for paragraph-targeted operations
   *
   * All edits in this section target this paragraph:
   * - For INSERT: Target paragraph to insert before/after
   * - For DELETE: Paragraph to delete
   * - For EDIT: Scopes searchPhrase search to ONLY this paragraph.
   *   searchPhraseOccurrence counts within this paragraph only (not document-wide).
   *
   * Index refers to context.document.body.paragraphs.items[]
   * Adjusted automatically for previous INSERT/DELETE operations via UnifiedIndexTracker
   */
  paragraphId?: number;

  /**
   * Type of operations in this section (SECTION-LEVEL)
   * - 'edit': Standard text modifications (DEFAULT)
   * - 'insert': Create new paragraphs with tracked changes
   * - 'delete': Remove entire paragraphs with tracked changes
   * All edits in this section share the same operation type
   * @default 'edit'
   */
  operationType?: EditOperationType;

  /**
   * Track changes mode captured when this AutoRedlineOutput was created
   * Used for deletion index adjustment calculations
   * - 'off': Track changes disabled, deletions shift indices
   * - 'trackAll': Track changes enabled, deletions only mark strikethrough
   * - 'trackMineOnly': Track changes enabled (only mine), same behavior as trackAll
   * @default 'off'
   *
   * This value is set by the backend and reflects the mode the edits
   * were generated under. The backend locks this mode in at request time and
   * uses it for every auto_redline_tool call within the same response, so all
   * edits in a multi-tool-call response are tied to the same original mode.
   * If the user toggles Track Changes while later tool results are still streaming back, a
   * fresh read would mark those later previews as captured under the toggled
   * mode and the apply-time revert in AutoRedlinePreview would skip when it
   * should fire, silently misplacing edits. Treat the backend value as the
   * source of truth for what representation these edits assume.
   */
  trackChangesMode?: TrackChangesMode;

  /**
   * How Word was DISPLAYING tracked changes when these edits were generated:
   * "Balloon", "Inline", "Mixed", or "unknown" when the add-in could not read
   * the setting.
   *
   * A separate axis from trackChangesMode. "Balloon" moves tracked deletions
   * out of the text layer that search() reads, so the backend builds both the
   * searchPhrases and searchParagraph against whichever layer this names. The
   * values are only valid for that layer, which is why the apply path re-reads
   * the live setting and refuses to apply against the other one.
   */
  markupMode?: string;

  /**
   * Native-Word-numbering column for the preview card (OUTPUT-LEVEL, sibling of
   * previewText). Rendered by the FE as a narrow column to the LEFT of
   * previewText so the user sees the paragraph's list numbering — and how a
   * redline changes it — separately from the paragraph text.
   *
   * - `null` / absent: the paragraph has no native numbering and nothing
   *   attaches one → render NO numbering column (single-column layout).
   * - Plain label (e.g. "2.b."): numbering exists and nothing changes it.
   * - Redline markup using the SAME inline-HTML convention as previewText:
   *   `<s style="color:#dc143c;">1.b.</s><u style="color:#00a86b;">2.b.</u>`
   *   renumbered; `<s>` only = numbering removed; `<u>` only = numbering
   *   gained. When other authors' tracked changes are present both tags use
   *   jade #00a86b (previewText's multi-author scheme).
   * - When the new label is underivable the backend emits a direction marker
   *   as the "new label" — "→" (demoted / joined a list) or "←" (promoted) —
   *   wrapped in the same `<u>` markup. Rendered like any other label.
   *
   * Labels arrive stripped (no leading indent spaces); column width/indent is
   * an FE styling concern.
   */
  numberingPreview?: string | null;

  /**
   * OUTPUT-LEVEL structured numbering change (sibling of `numberingPreview`).
   * When present, the FE renders a Word-Ribbon-style Fluent icon + tooltip in
   * the numbering gutter (plus a muted echoed prior-state label from
   * `currentLabel`) INSTEAD of the `numberingPreview` HTML gutter. The two are
   * MUTUALLY EXCLUSIVE; render precedence is `numberingChange` →
   * `numberingPreview` → single-column. `null`/absent = no icon gutter.
   */
  numberingChange?: NumberingChange | null;

  /** Array of edit operations to apply */
  edits: EditOperation[];

  /** Original tool_use_id from the Anthropic API (for conversation history reconstruction) */
  tool_use_id?: string;

  /** CS's original tool input parameters (for conversation history reconstruction) */
  tool_input?: Record<string, unknown>;

  /**
   * Per-edit apply outcomes keyed by editId, written by AutoRedlinePreview after
   * an apply attempt. Never present on backend payloads or old sessions — absence
   * of the field (or of an editId entry) means "pending". editIds are only unique
   * WITHIN an output, which is why this map lives on the output rather than at
   * the inline-tool-event level.
   */
  editStatuses?: Record<string, PersistedEditOutcome>;
}

/**
 * A terminal per-edit apply outcome persisted onto the owning AutoRedlineOutput
 * (inside message.inlineToolEvents) so it survives component remounts
 * (tab change / agent switch) and feeds conversation history (addComment pattern).
 * Only terminal states are persisted — transient "pending"/"applying" are
 * represented by the ABSENCE of an entry, so a mid-apply unmount never
 * rehydrates a stuck "applying" card.
 */
export interface PersistedEditOutcome {
  status: "applied" | "failed";

  /** Failure reason when status === "failed" */
  errorMessage?: string;

  /** Timestamp when the edit was applied (status === "applied") */
  appliedAt?: number;
}

/**
 * Extended edit operation with UI state tracking
 * Used internally by components to track application status
 */
export interface EditOperationWithStatus extends EditOperation {
  /** Current status of this edit operation */
  status: EditStatus;

  /** Error message if the edit failed to apply */
  errorMessage?: string;

  /** Timestamp when the edit was applied */
  appliedAt?: number;
}

/**
 * Configuration for composer mode behavior
 */
export interface ComposerModeConfig {
  /** Delay between applying edits in milliseconds */
  delayBetweenEdits: number;

  /** Whether to stop on first error or continue */
  continueOnError: boolean;

  /** Whether to show preview before auto-applying */
  showPreviewFirst: boolean;

  /** Delay before starting auto-apply after message completes */
  initialDelay: number;
}

/**
 * Default composer mode configuration
 */
export const DEFAULT_COMPOSER_CONFIG: ComposerModeConfig = {
  delayBetweenEdits: 750, // 750ms between edits
  continueOnError: true, // Continue applying remaining edits even if one fails
  showPreviewFirst: true, // Show preview before auto-applying
  initialDelay: 500, // 500ms delay before starting auto-apply
};

/**
 * Type guard to check if an object is an AutoRedlineOutput
 */
export function isAutoRedlineOutput(obj: any): obj is AutoRedlineOutput {
  if (
    !obj ||
    typeof obj !== "object" ||
    typeof obj.sectionName !== "string" ||
    typeof obj.previewText !== "string" ||
    typeof obj.searchParagraph !== "string" ||
    !Array.isArray(obj.edits) ||
    !obj.edits.every(isEditOperation)
  ) {
    return false;
  }

  // Validate operationType if present
  if (obj.operationType !== undefined) {
    if (!["edit", "insert", "delete"].includes(obj.operationType)) {
      return false;
    }
  }

  // Validate trackChangesMode if present
  if (obj.trackChangesMode !== undefined) {
    if (!["off", "trackAll", "trackMineOnly"].includes(obj.trackChangesMode)) {
      return false;
    }
  }

  // Validate paragraphId if present (must be non-negative number)
  if (obj.paragraphId !== undefined && obj.paragraphId !== null) {
    if (typeof obj.paragraphId !== "number" || obj.paragraphId < 0) {
      return false;
    }
  }

  // Validate numberingPreview if present (string | null; null = no numbering column)
  if (obj.numberingPreview !== undefined && obj.numberingPreview !== null) {
    if (typeof obj.numberingPreview !== "string") {
      return false;
    }
  }

  // Validate numberingChange if present (NumberingChange | null; null = no icon
  // gutter). A false negative silently drops the whole redline at the transport
  // gate, so validate the SHARED contract exactly: kind is one of the four
  // strings; optional currentLabel is string; optional fromLevel/toLevel are
  // numbers.
  if (obj.numberingChange !== undefined && obj.numberingChange !== null) {
    const nc = obj.numberingChange;
    if (typeof nc !== "object") return false;
    if (!["indentIn", "indentOut", "addToList", "newList"].includes(nc.kind)) return false;
    // The backend serializes optional fields as explicit `null` (pydantic
    // model_dump), not by omitting them, so tolerate null as well as undefined —
    // otherwise a valid payload like {kind, currentLabel: null, fromLevel: null}
    // fails the guard and the whole redline is dropped at the transport gate.
    if (nc.currentLabel != null && typeof nc.currentLabel !== "string") return false;
    if (nc.fromLevel != null && typeof nc.fromLevel !== "number") return false;
    if (nc.toLevel != null && typeof nc.toLevel !== "number") return false;
  }

  return true;
}

/**
 * Type guard to check if an object is an EditOperation
 * Note: targetOccurrence field is optional and defaults to 1 if not provided
 * searchPhraseOccurrence is REQUIRED (no backward compatibility)
 */
export function isEditOperation(obj: any): obj is EditOperation {
  if (!obj || typeof obj !== "object") return false;
  if (typeof obj.editId !== "string") return false;

  const hasListChange = obj.listChange !== undefined && obj.listChange !== null;

  // Text fields are strictly required for classic text edits. For structure-only
  // edits that carry a `listChange`, the backend may omit them (or send empty
  // strings), so we only type-check them when present — otherwise a valid
  // structure-only edit would be silently dropped at the transport gate.
  if (hasListChange) {
    if (obj.searchPhrase !== undefined && typeof obj.searchPhrase !== "string") return false;
    if (obj.replacementText !== undefined && typeof obj.replacementText !== "string") return false;
    if (obj.targetText !== undefined && typeof obj.targetText !== "string") return false;
    if (obj.position !== undefined && !["replace", "before", "after"].includes(obj.position)) {
      return false;
    }
    if (
      obj.searchPhraseOccurrence !== undefined &&
      (typeof obj.searchPhraseOccurrence !== "number" || obj.searchPhraseOccurrence < 1)
    ) {
      return false;
    }
  } else {
    if (
      typeof obj.searchPhrase !== "string" ||
      typeof obj.replacementText !== "string" ||
      !["replace", "before", "after"].includes(obj.position)
    ) {
      return false;
    }
    // targetText can be empty string for insertions
    if (typeof obj.targetText !== "string") return false;
    // searchPhraseOccurrence is REQUIRED (must be number >= 1)
    if (typeof obj.searchPhraseOccurrence !== "number" || obj.searchPhraseOccurrence < 1) {
      return false;
    }
  }

  // Optional fields with defaults
  if (obj.matchWholeWord !== undefined && typeof obj.matchWholeWord !== "boolean") {
    return false;
  }

  // targetOccurrence is optional (defaults to 1 if not provided)
  if (obj.targetOccurrence !== undefined && typeof obj.targetOccurrence !== "number") {
    return false;
  }

  // requiresSync is optional (used for backend-detected dependencies)
  if (obj.requiresSync !== undefined && typeof obj.requiresSync !== "boolean") {
    return false;
  }

  // listChange is optional; validate its shape when present
  if (hasListChange) {
    const lc = obj.listChange;
    if (typeof lc !== "object") return false;
    if (!["setLevel", "newList", "continueList", "detach"].includes(lc.action)) return false;
    if (lc.level !== undefined && lc.level !== null) {
      if (typeof lc.level !== "number" || lc.level < 0 || lc.level > 8) return false;
    }
    if (lc.anchorParagraphId !== undefined && lc.anchorParagraphId !== null) {
      if (typeof lc.anchorParagraphId !== "number" || lc.anchorParagraphId < 0) return false;
    }
  }

  return true;
}

/**
 * Validates occurrence parameters for an edit operation
 * Throws descriptive errors if validation fails - used during edit application
 *
 * @param edit - The edit operation to validate
 * @param foundSearchPhraseCount - Number of searchPhrase occurrences found in document
 * @param foundTargetTextCount - Number of targetText occurrences found within searchPhrase (optional)
 * @throws Error with descriptive message including edit ID and actual vs expected counts
 */
export function validateOccurrences(
  edit: EditOperation,
  foundSearchPhraseCount: number,
  foundTargetTextCount?: number
): void {
  // Validate searchPhraseOccurrence
  if (edit.searchPhraseOccurrence > foundSearchPhraseCount) {
    throw new Error(
      `Edit ${edit.editId}: searchPhraseOccurrence is ${edit.searchPhraseOccurrence} ` +
        `but only ${foundSearchPhraseCount} occurrence(s) of "${edit.searchPhrase}" found in document. ` +
        `Backend calculated ${edit.searchPhraseOccurrence}, but document has changed or calculation was incorrect.`
    );
  }

  // Validate targetOccurrence (if applicable)
  if (edit.position === "replace" && edit.targetText && foundTargetTextCount !== undefined) {
    const targetOcc = edit.targetOccurrence ?? 1;
    if (targetOcc > foundTargetTextCount) {
      throw new Error(
        `Edit ${edit.editId}: targetOccurrence is ${targetOcc} ` +
          `but only ${foundTargetTextCount} occurrence(s) of "${edit.targetText}" found within searchPhrase. ` +
          `This indicates the searchPhrase or targetText does not match the document state.`
      );
    }
  }
}

/**
 * Result type for edit application
 */
export interface EditApplicationResult {
  /** Whether the edit was successfully applied */
  success: boolean;

  /** Error message if the edit failed */
  error?: string;

  /** The edit that was attempted */
  editId: string;

  /** Time taken to apply the edit in milliseconds */
  duration?: number;
}

/**
 * Batch result for multiple edit applications
 */
export interface BatchEditResult {
  /** Successfully applied edits */
  successful: EditOperation[];

  /** Failed edits with error details */
  failed: Array<{
    edit: EditOperation;
    error: string;
  }>;

  /** Total time taken in milliseconds */
  totalDuration: number;
}
